import ShapeBase from './ShapeBase';
import moment from 'moment';
import Color from 'color';

import {
  getInterval,
  getNormalizedIntervals,
  getPixelsPerDay,
} from '../utils/timeline/helpers';
import tools from '../tools';

import { type TimelineInterval, type TimelineRenderSettings } from '../types';
import TimelineDateControl from '../controls/TimelineDateControl';
import { dateFiscal } from '../utils/timeline/dateFns';
import trackEvent from '../utils/analytics';
import shapeTimeline from '../assets/icons/shapeTimeline.svg';
import { svgToIcon } from '../utils/svg';
import type Shape from '../objects/Shape';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';

const SIDES = {
  left: -1,
  right: 1,
};

// Minimum number of pixels per day for the day of the week to be displayed
// alongside the day of the month.
const FULL_DAY_DISPLAY_PIXELS_PER_DAY = 30;

// Padding added to the left of the pinned elements to the left of the timeline
// when they're visible.
const PINNED_ELEMENT_PADDING = 16;

const SECONDARY_COLOR = '#999999';

const VERTICAL_TICK_SIZE = 13;
const LARGE_VERTICAL_TICK_SIZE = 19;
const VERTICAL_TICK_OFFSET = 6;
const LARGE_VERTICAL_TICK_OFFSET = 8;

class ShapeTimeline extends ShapeBase {
  // Width of the object prior to scaling. Used for expanding/contracting the
  // date range.
  originalWidth: number;

  // Base rendering unit of a timeline.
  pixelsPerDay: number;

  // Indicates if the user is changing the date ranges by dragging. When this
  // is the case certain recalculations are skipped.
  changingDateRange: boolean;

  // Indicates that the date range was manipulated via the handles. It is
  // used to track the analytics event only when the user lets go of the handles.
  dateRangeChangedViaHandles: boolean;

  // Time intervals to be rendered.
  intervals: TimelineInterval[];

  // These settings are used for everything that's rendered outside of the
  // element itself for the purposes of smooth date range adjustments.
  renderSettings: TimelineRenderSettings;

  constructor(fabricObject: Shape) {
    super(fabricObject);

    fabricObject.on('scaling', eventData => {
      const pixelsPerDay = this.getPixelsPerDay();

      if (this.changingDateRange) {
        this.dateRangeChangedViaHandles = true;
        this.fabricObject.hasControls = true;
        this.adjustDateRange(eventData);
      } else {
        this.pixelsPerDay = pixelsPerDay;
        this.renderSettings = this.getRenderSettings();
        this.intervals = this.getIntervals();
      }
    });

    fabricObject.on('scaled', () => {
      // Update start and end dates
      // The start date should always fall on an exact date,
      // the end date may fall on a partial date
      this.originalWidth = fabricObject.getScaledWidth();

      if (this.dateRangeChangedViaHandles) {
        trackEvent(
          'Feature-7177137609894815136',
          'User edits timeline duration',
          {
            duration: 'Custom via handles',
          }
        );
        this.dateRangeChangedViaHandles = false;
      }
    });

    fabricObject.on('added', () => {
      this.originalWidth = fabricObject.getScaledWidth();
      this.renderSettings = this.getRenderSettings();
      this.intervals = this.getIntervals();
    });

    fabricObject.on('removed', () => {
      trackEvent('Feature-7177137609894815136', 'User deletes timeline');
    });
  }

  get startDate() {
    return moment.unix(this.fabricObject.startDate!);
  }

  get endDate() {
    return moment.unix(this.fabricObject.endDate!);
  }

  get pixelsPerSecond() {
    return this.pixelsPerDay / 86400;
  }

  get settings() {
    const obj = this.fabricObject;

    const startDate = this.startDate.format('YYYY-MM-DD');
    const endDate = this.endDate.format('YYYY-MM-DD');

    return {
      visibleStartDate: startDate,
      visibleEndDate: endDate,
      startDate,
      endDate,
      hideWeekends: !!obj.hideWeekends,
      timescale: obj.timescale!,
      fiscalMonth: obj.fiscalMonth!,
      fiscalYearFormat: obj.fiscalYearFormat!,
    };
  }

  get primaryColor() {
    const { fill } = this.fabricObject;

    if (!fill || fill === '#FFFFFF') {
      return '#E1E1E1';
    }

    return fill;
  }

  get mutedColor() {
    const { fill } = this.fabricObject;
    if (!fill) {
      return 'rgba(255,255,255,0)';
    }
    return Color(this.primaryColor).mix(Color('white'), 0.8).hex();
  }

  // Remove rotation control. Rotation on timelines doesn't really make
  // that much sense and makes rendering the date change controls way more
  // complex.
  static shapeControls = {
    ...this.createDefaultControls({ noRotate: true }),
    cl: new TimelineDateControl({ x: -0.5, y: 0 }),
    cr: new TimelineDateControl({ x: 0.5, y: 0 }),
  };

  static defaultWidth() {
    return 800;
  }

  static defaultHeight() {
    return 89;
  }

  static renderIcon() {
    return svgToIcon(shapeTimeline);
  }

  // No attachments on timelines.
  attachmentPoints() {
    return [];
  }

  static outboundConnectControlDefinitions() {
    return {};
  }

  wasSet(keyOrPath: KeyPath, value: unknown): void {
    super.wasSet(keyOrPath, value);

    if (
      typeof keyOrPath === 'string' &&
      ['timescale', 'startDate', 'endDate', 'scaleX', 'scaleY'].includes(
        keyOrPath
      )
    ) {
      // This should be done only when setting using the range picker
      if (!this.changingDateRange) {
        this.pixelsPerDay = this.getPixelsPerDay();
        this.renderSettings = this.getRenderSettings();
      }
      this.intervals = this.getIntervals();
    }

    if (keyOrPath === 'fill') {
      trackEvent('Feature-7177137609894815136', 'User sets timeline color');
    }
  }

  changeDateRange(func) {
    try {
      this.changingDateRange = true;
      func();
    } catch {
      return false;
    } finally {
      this.changingDateRange = false;
    }

    return true;
  }

  getRenderSettings(): TimelineRenderSettings {
    // The render settings are used to ensure a smooth user experience while
    // dragging to change the date range. Additional dates are rendered outside
    // of the shape and clipped during rendering, when the user drags the
    // shape, the additional dates are revealed.
    //
    // Start date: Starts at an earlier date than the user set start date by
    // one unit of the major interval.
    //
    // End Date: Starts at a latter date than the user set end date by one unit
    // of the major interval.
    //
    // Offset: Inidcates where rendering should start so that the first date
    // rendered within the shape is the user set start date.
    const obj = this.fabricObject;
    const [major] = getNormalizedIntervals({
      visibleWidth: obj.getScaledWidth(),
      ...this.settings,
    });

    if (!this.pixelsPerDay) {
      this.pixelsPerDay = this.getPixelsPerDay();
    }

    const renderStartDate = this.startDate
      .subtract(1, major)
      .format('YYYY-MM-DD');
    const renderEndDate = this.endDate.add(1, major).format('YYYY-MM-DD');

    const offset =
      this.startDate.diff(renderStartDate, 'seconds') * this.pixelsPerSecond;
    return {
      ...this.settings,
      startDate: renderStartDate,
      endDate: renderEndDate,
      offset,
    };
  }

  getPixelsPerDay() {
    const obj = this.fabricObject;
    return (
      getPixelsPerDay({
        ...this.settings,
        width: obj.getScaledWidth(),
      }) / obj.scaleY
    );
  }

  tools() {
    return [tools.ToolFillColor, tools.ToolTimescale, tools.ToolDateRange];
  }

  allowedStyleCopyPasteFields(): string[] {
    return ['opacity', 'fill'];
  }

  adjustDateRange(eventData) {
    if (!this.pixelsPerDay) {
      return;
    }

    const obj = this.fabricObject;
    const { signX: side } = eventData.transform;
    const widthDifference =
      (obj.getScaledWidth() - this.originalWidth) / obj.scaleY;
    const secondsChange = widthDifference / this.pixelsPerSecond;

    const dateToChange = side === SIDES.left ? 'startDate' : 'endDate';
    const newTimestamp = obj[dateToChange]! + side * secondsChange;
    const newDate = moment.unix(newTimestamp);

    const renderDaysDifference = Math.abs(
      newDate.diff(this.renderSettings[dateToChange], 'days')
    );

    // Update the start or end date if we are about to run out of days
    // or if we're going to leave too many extra days outside of the
    // rendering area.
    if (renderDaysDifference <= 1 || renderDaysDifference >= 8) {
      this.renderSettings = this.getRenderSettings();
    }

    // We want to expand the date range while keeping the current pixels per
    // day and interval values.
    obj.set(dateToChange, newTimestamp);
    this.originalWidth = obj.getScaledWidth();

    if (side === SIDES.left) {
      this.renderSettings.offset -= widthDifference;
    }
  }

  getIntervals(): TimelineInterval[] {
    const obj = this.fabricObject;
    const width = obj.getScaledWidth() / obj.scaleY;
    const settings = this.settings;

    const intervals = getNormalizedIntervals({
      visibleWidth: width,
      ...settings,
    });

    return intervals.map(intervalType => ({
      intervalType,
      interval: getInterval(intervalType, this.renderSettings),
    }));
  }

  hasEmbeddedText() {
    return false;
  }

  renderIntervals(ctx, x, y) {
    this.intervals.forEach((interval, i) => {
      switch (interval.intervalType) {
        case 'years':
          this.renderYears(ctx, interval, i, x, y);
          break;
        case 'quarters':
          this.renderQuarters(ctx, interval, i, x, y);
          break;
        case 'months':
          this.renderMonths(ctx, interval, i, x, y);
          break;
        case 'weeks':
          this.renderWeeks(ctx, interval, i, x, y);
          break;
        case 'days':
          this.renderDays(ctx, interval, i, x, y);
          break;
      }
    });
  }

  renderHorizontalDivider(ctx, y) {
    const obj = this.fabricObject;
    const x = obj.getScaledWidth() / 2 / obj.scaleY;

    ctx.strokeStyle = this.primaryColor;
    ctx.lineWidth = 1 / obj.scaleY;
    ctx.moveTo(-x, y);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  renderYears(ctx, years, index, x, y) {
    const {
      interval: { intervals, format },
    } = years;
    const yOffset = 35;

    ctx.save();
    ctx.font = `600 24px inter, sans-serif`;
    ctx.fillStyle = '#333333';
    intervals.forEach(({ startOfInterval, daysCount }) => {
      const intervalWidth = this.pixelsPerDay * daysCount;
      const nextTick = x + intervalWidth;

      let renderX = x;
      const obj = this.fabricObject;
      const startOfContainer = -obj.getScaledWidth() / 2 / obj.scaleY;
      const pinnedRenderX = startOfContainer + PINNED_ELEMENT_PADDING;
      if (x < pinnedRenderX && nextTick > startOfContainer + 65) {
        renderX = pinnedRenderX;
      }

      ctx.fillText(format(startOfInterval), renderX, y + yOffset);

      x += intervalWidth;
    });

    const horizontalDividerOffset = 12;
    const dividerY = y + yOffset + horizontalDividerOffset;
    this.renderHorizontalDivider(ctx, dividerY);

    ctx.restore();
  }

  renderQuarters(ctx, quarters, index, x, y) {
    const {
      interval: { intervals },
    } = quarters;

    // Quarters is the highest level in a 3 level hierarchy.
    let fontSize = '20px';
    let yOffset = 25;
    let color = '#333333';

    let renderYear = true;
    let yearFontSize = '14px';
    let yearOffset = 30;

    let renderHorizontalDivider = true;
    let renderVerticalDividers = false;
    let horizontalDividerOffset = 8;

    // Quarters is the highest level in a 2 level hierarchy.
    if (this.intervals.length === 2 && index === 0) {
      fontSize = '24px';
      yearFontSize = '14px';
      yearOffset = 34;
      yOffset = 35;
      horizontalDividerOffset = 12;

      // Quarters is the lowest level in a 2 level hierarchy.
    } else if (this.intervals.length === 2) {
      fontSize = '18px';
      renderYear = false;
      renderHorizontalDivider = false;
      renderVerticalDividers = true;
      horizontalDividerOffset = 9;
      yOffset = 80;
      color = '#333333';
    }

    ctx.save();
    ctx.font = `600 ${fontSize} inter, sans-serif`;
    ctx.fillStyle = color;
    let lastRenderedYear: number | null = null;
    let firstQuarterInContainer = true;
    const renderY = y + yOffset;

    intervals.forEach(({ startOfInterval, daysCount }) => {
      const intervalWidth = this.pixelsPerDay * daysCount;
      const nextTick = x + intervalWidth;
      const obj = this.fabricObject;

      const { quarter, year } = dateFiscal(startOfInterval);
      const quarterText = `Q${quarter}`;
      const verticalDividerOffset = renderVerticalDividers
        ? LARGE_VERTICAL_TICK_OFFSET
        : 0;

      // Pin the Quarter label to the left side of the timeline if it would
      // have been rendered outside of the container and there's space to
      // accomodate it.
      let renderX = x + verticalDividerOffset;
      const startOfContainer =
        -this.fabricObject.getScaledWidth() / 2 / obj.scaleY;
      const pinnedRenderX = startOfContainer + PINNED_ELEMENT_PADDING;
      const textWidth = ctx.measureText(quarterText).width;
      if (x < pinnedRenderX && nextTick > startOfContainer + textWidth + 17) {
        renderX = pinnedRenderX;
      }

      ctx.fillText(quarterText, renderX, renderY);

      // The year is rendered alongside the quarter only when Quarter is the
      // lowest resolution level (i.e. when resolution is set to months, weeks
      // or days), And there's space to accomodate it. Additionally, each year
      // is only displayed once, next to the first visible Quarter of a year.
      if (
        renderYear &&
        year !== lastRenderedYear &&
        nextTick > renderX + yearOffset + 40 &&
        renderX >= startOfContainer
      ) {
        ctx.save();
        ctx.font = `500 ${yearFontSize} inter, sans-serif`;
        ctx.fillStyle = '#999999';
        ctx.fillText(year, renderX + yearOffset, renderY);
        ctx.restore();
        lastRenderedYear = year;
      }

      // We only render the vertical dividers when the quarters aren't the
      // highest level in the hierarchy. Additionally, showing the vertical
      // divider close to the edge isn't visually appealing, we only want it
      // to be rendered for the second quarter onward.
      if (
        renderVerticalDividers &&
        renderX > startOfContainer &&
        !firstQuarterInContainer
      ) {
        ctx.beginPath();
        ctx.moveTo(
          renderX - verticalDividerOffset,
          renderY + horizontalDividerOffset
        );
        ctx.strokeStyle = this.primaryColor;
        ctx.lineWidth = 1 / obj.scaleY;
        ctx.lineTo(
          renderX - verticalDividerOffset,
          renderY - LARGE_VERTICAL_TICK_SIZE
        );
        ctx.stroke();
      } else if (renderVerticalDividers && renderX > startOfContainer) {
        firstQuarterInContainer = false;
      }

      x += intervalWidth;
    });

    if (renderHorizontalDivider) {
      const dividerY = renderY + horizontalDividerOffset;
      this.renderHorizontalDivider(ctx, dividerY);
    }

    ctx.restore();
  }

  renderMonths(ctx, months, index, x, y) {
    const {
      interval: { intervals, format },
      intervalType,
    } = months;

    // Months is the second element in a 3 level hierarchy
    let fontSize = '12px';
    let yOffset = 55;
    let renderHorizontalDivider = true;
    let horizontalDividerOffset = 5;
    let verticalDividerSize = VERTICAL_TICK_SIZE;
    let verticalDividerOffset = VERTICAL_TICK_OFFSET;

    // Months is the second element in a 2 level hierarchy
    if (this.intervals.length === 2) {
      fontSize = '16px';
      yOffset = 80;
      renderHorizontalDivider = false;
      horizontalDividerOffset = 9;
      verticalDividerSize = LARGE_VERTICAL_TICK_SIZE;
      verticalDividerOffset = LARGE_VERTICAL_TICK_OFFSET;
    }

    ctx.save();
    ctx.font = `700 ${fontSize} inter, sans-serif`;
    let firstMonthInContainer = true;
    intervals.forEach(({ startOfInterval, daysCount }) => {
      const obj = this.fabricObject;
      const intervalWidth = this.pixelsPerDay * daysCount;
      const month = format(startOfInterval, intervalType).toUpperCase();

      // Pin the month label to the left side of the timeline if it would
      // have been rendered outside of the container and there's space to
      // accomodate it.
      let renderX = x + VERTICAL_TICK_OFFSET;
      const renderY = y + yOffset;
      const startOfContainer =
        -this.fabricObject.getScaledWidth() / 2 / obj.scaleY;
      const pinnedRenderX = startOfContainer + PINNED_ELEMENT_PADDING;
      const nextTick = x + intervalWidth;
      const textWidth = ctx.measureText(month).width;

      if (x < pinnedRenderX && nextTick > startOfContainer + textWidth + 18) {
        renderX = pinnedRenderX;
      } else if (nextTick <= startOfContainer + textWidth + 7) {
        // At some scales the month jumps back to its original position and
        // remains visible, causing a feeling of jankiness. Render outside of
        // the container instead.
        renderX = startOfContainer - textWidth - 7;
      }

      // Showing the vertical divider close to the edge isn't visually
      // appealing, we only want it to be rendered for the second month onward.
      if (renderX > startOfContainer && !firstMonthInContainer) {
        ctx.beginPath();
        ctx.moveTo(
          renderX - verticalDividerOffset,
          renderY + horizontalDividerOffset
        );
        ctx.strokeStyle = this.primaryColor;
        ctx.lineWidth = 1 / obj.scaleY;
        ctx.lineTo(
          renderX - verticalDividerOffset,
          renderY - verticalDividerSize
        );
        ctx.stroke();
      } else if (renderX > startOfContainer) {
        firstMonthInContainer = false;
      }

      ctx.fillText(month, renderX, renderY);

      x += intervalWidth;
    });

    if (renderHorizontalDivider) {
      const dividerY = y + yOffset + horizontalDividerOffset;
      this.renderHorizontalDivider(ctx, dividerY);
    }

    ctx.restore();
  }

  renderWeeks(ctx, weeks, index, x, y) {
    const {
      interval: { intervals, format },
      intervalType,
    } = weeks;
    const yOffset = 80;

    ctx.save();
    ctx.font = '500 11px inter, sans-serif';
    intervals.forEach(({ startOfInterval, daysCount }) => {
      const intervalWidth = this.pixelsPerDay * daysCount;
      ctx.fillText(format(startOfInterval, intervalType), x, y + yOffset);

      x += intervalWidth;
    });
    ctx.restore();
  }

  renderDays(ctx, days, index, x, y) {
    const {
      interval: { intervals, format },
      intervalType,
    } = days;
    const yOffset = 80;

    ctx.save();
    ctx.font = '500 11px inter, sans-serif';
    intervals.forEach(({ startOfInterval, daysCount }) => {
      const intervalWidth = this.pixelsPerDay * daysCount;
      const renderY = y + yOffset;
      let dayOffset = 0;

      // When there's enough space to accomodate them, the day of the week is
      // renderd alongside the day of the month.
      if (this.pixelsPerDay >= FULL_DAY_DISPLAY_PIXELS_PER_DAY) {
        const weekDay = format(startOfInterval, intervalType);

        ctx.save();
        const { width: weekDayWidth } = ctx.measureText(weekDay);
        ctx.fillStyle = SECONDARY_COLOR;
        ctx.fillText(weekDay, x, renderY);
        ctx.restore();
        dayOffset = weekDayWidth + 2;
      }

      const monthDay = moment(startOfInterval).format('D');
      ctx.fillText(monthDay, x + dayOffset, renderY);

      x += intervalWidth;
    });
    ctx.restore();
  }

  render(ctx: CanvasRenderingContext2D) {
    if (!this.intervals.length) {
      return;
    }

    ctx.save();

    const obj = this.fabricObject;
    // The scale values are determined through two operations:
    // 1. Divide each one by the object's respective scale i.e. 1/scaleX and 1/scaleY. This makes rendering independent of the scales.
    // 2. Multiply each by scaleY to make the scale Y the controlling factor in the element's scaling.
    ctx.scale(obj.scaleY / obj.scaleX, 1);

    // Horizontal distances must take into account the fact that the context
    // horizontal scaling is multiplied by the Y scale.
    const x = -obj.getScaledWidth() / 2 / obj.scaleY;
    const y = -obj.height / 2;
    const backgroundWidth = obj.getScaledWidth() / obj.scaleY;
    const backgroundHeight = obj.height;
    ctx.save();
    ctx.fillStyle = this.mutedColor;
    ctx.strokeStyle = this.primaryColor;
    ctx.lineWidth = 1 / obj.scaleY;
    ctx.shadowBlur = 8;
    ctx.shadowColor = 'rgba(0,0,0,0.08)';
    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(x, y, backgroundWidth, backgroundHeight, 4);
    } else {
      ctx.rect(x, y, backgroundWidth, backgroundHeight);
    }
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // Creates a clipping rectangle with the same dimensions as the original
    // element, removing the dates intentionally rendered outside of the object.
    // Clipping must be done after the background has been rendered so that we
    // don't clip the shadow.
    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(x, y, backgroundWidth, backgroundHeight, 4);
    } else {
      ctx.rect(x, y, backgroundWidth, backgroundHeight);
    }
    ctx.clip();

    const { offset } = this.renderSettings;
    this.renderIntervals(ctx, x - offset, y);

    ctx.restore();
  }
}

export default ShapeTimeline;
